import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const steps = [
    {
        number: "01",
        title: "Bezpłatny Pomiar",
        description: "Przyjeżdżamy na budowę w dogodnym terminie. Dokładnie mierzymy powierzchnię, oceniamy stan dachu i doradzamy najlepsze systemy dociepleń i wykończenia (styropapa, płyty PIR).",
        color: "bg-dark",
        textColor: "text-background",
        accentColor: "text-accent"
    },
    {
        number: "02",
        title: "Wycena i Umowa",
        description: "Otrzymujesz przejrzysty kosztorys bez ukrytych opłat. Cena, którą widzisz na umowie, to cena, którą płacisz po zakończeniu prac. Blokujemy stawkę dla Twojego spokoju.",
        color: "bg-[#1A1A1A]",
        textColor: "text-dark",
        accentColor: "text-white"
    },
    {
        number: "03",
        title: "Szybki Montaż",
        description: "Nasza autoryzowana ekipa wkracza do akcji. Sprawnie i czysto wykonujemy docieplenie oraz krycie papą termozgrzewalną. Zostawiamy po sobie absolutny porządek i dajemy pełną gwarancję szczelności.",
        color: "bg-accent",
        textColor: "text-background",
        accentColor: "text-[#4A1F12]"
    }
];

export default function Protocol() {
    const sectionRef = useRef(null);
    const containerRef = useRef(null);
    const cardsRef = useRef([]);

    useEffect(() => {
        // Only apply complex scroll pinning on desktop
        let ctx = gsap.context(() => {
            const mm = gsap.matchMedia();

            mm.add("(min-width: 1024px)", () => {
                const cards = cardsRef.current;
                const totalCards = cards.length;

                // Create a master timeline for the pinning sequence
                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: containerRef.current,
                        start: "top top",
                        // The duration of the pin depends on the number of cards
                        end: `+=${100 * totalCards}%`,
                        pin: true,
                        pinSpacing: true,
                        scrub: 1,
                        id: "protocol-pin"
                    }
                });

                // Animate each card overlaying the previous one
                cards.forEach((card, index) => {
                    if (index === 0) return; // First card is already visible

                    // Card slides up from the bottom of the container
                    tl.fromTo(card,
                        { yPercent: 100 },
                        { yPercent: 0, ease: "none" }
                    );

                    // Simultaneously, scale down and fade the card *underneath*
                    tl.to(cards[index - 1], {
                        scale: 0.9,
                        opacity: 0.06,
                        ease: "none"
                    }, "<"); // The "<" ensures it happens at the same time as the previous animation
                });
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} className="relative bg-background">
            {/* Mobile Title View (hidden on desktop pinned section, shown inline) */}
            <div className="lg:hidden py-16 px-6 text-center">
                <span className="font-mono text-xs uppercase tracking-widest text-[#E3000F] font-bold mb-4 block">Protokoł TOBMAR</span>
                <h2 className="font-heading font-black text-4xl text-dark leading-tight uppercase tracking-tighter">
                    Droga do Szczelnego Dachu
                </h2>
            </div>

            <div ref={containerRef} className="relative lg:h-[100vh] w-full overflow-hidden flex flex-col lg:block">

                {steps.map((step, idx) => (
                    <div
                        key={idx}
                        ref={el => cardsRef.current[idx] = el}
                        className={`
              w-full lg:absolute lg:top-0 lg:left-0 lg:h-[100vh] flex items-center justify-center p-6 md:p-16 lg:p-24
              ${step.color}
              min-h-[60vh] lg:min-h-0 relative origin-top py-20 lg:py-0 rounded-t-[3rem] lg:rounded-none mt-[-2rem] lg:mt-0 first:mt-0 shadow-[0_-20px_50px_rgba(0,0,0,0.1)] lg:shadow-none
            `}
                        style={{ zIndex: idx }}
                    >
                        <div className="max-w-7xl mx-auto w-full flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-24">

                            {/* Left Side: Number & Title */}
                            <div className="w-full lg:w-1/2">
                                <div className={`font-mono text-xl md:text-2xl mb-6 ${step.accentColor} font-bold tracking-widest uppercase`}>
                                    Faza {step.number}
                                </div>
                                <h2 className={`font-heading font-black text-[clamp(2.5rem,5vw,5rem)] ${step.textColor} leading-[0.9] tracking-tighter uppercase mb-8`}>
                                    {step.title}
                                </h2>
                                <div className={`w-24 h-1 ${step.accentColor} opacity-50`}></div>
                            </div>

                            {/* Right Side: Description */}
                            <div className="w-full lg:w-1/2">
                                <p className={`font-body text-xl md:text-2xl lg:text-3xl ${step.textColor} opacity-90 leading-relaxed font-light`}>
                                    {step.description}
                                </p>
                            </div>

                        </div>
                    </div>
                ))}

            </div>
        </section>
    );
}
